import { useCallback, useEffect, useState } from "react";
import { Shield, Loader2, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([api.get("/admin/roles"), api.get("/admin/admins")]);
      setRoles(r.data.roles);
      setAdmins(a.data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const assign = async (uid, admin_role) => {
    try {
      await api.put(`/admin/admins/${uid}/role`, { admin_role });
      toast.success("Admin role updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  if (denied) return <p className="text-sm text-[#71717A]" data-testid="admin-roles-denied">Only administrators with the "roles.manage" permission can manage roles.</p>;
  if (loading) return <div className="flex justify-center py-20 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div data-testid="admin-roles" className="space-y-8">
      {/* Admin users */}
      <div>
        <h3 className="text-white font-medium mb-3">Administrators ({admins.length})</h3>
        <div className="rounded-2xl border border-white/8 bg-[#121214] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-roles-table">
              <thead><tr className="border-b border-white/8 text-left text-[#71717A]">
                <th className="px-5 py-3 font-normal">Name</th><th className="px-5 py-3 font-normal">Email</th>
                <th className="px-5 py-3 font-normal">Assigned role</th>
              </tr></thead>
              <tbody>
                {admins.map((a) => (
                  <tr key={a.user_id} className="border-b border-white/5 last:border-0" data-testid={`admin-role-row-${a.user_id}`}>
                    <td className="px-5 py-3 text-white">{a.name || "—"}</td>
                    <td className="px-5 py-3 text-[#A1A1AA]">{a.email}</td>
                    <td className="px-5 py-3">
                      <Select value={a.admin_role} onValueChange={(v) => assign(a.user_id, v)}>
                        <SelectTrigger className="w-52 bg-[#0d0d0f] border-white/10 text-white h-9" data-testid={`admin-role-select-${a.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">
                          {roles.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`role-opt-${r.id}`}>{r.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Role matrix */}
      <div>
        <h3 className="text-white font-medium mb-3">Role permissions</h3>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {roles.map((r) => (
            <div key={r.id} className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid={`role-card-${r.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-white font-medium">{r.label}</span>
              </div>
              <p className="text-xs text-[#71717A] mb-3">{r.description}</p>
              <div className="flex flex-wrap gap-1.5">
                {r.permissions.map((p) => (
                  <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[#A1A1AA] flex items-center gap-1">
                    <Check className="w-2.5 h-2.5 text-emerald-400" />{p}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
